import React from "react";
import { Navigate, Route } from "react-router-dom";

// profile
import { ApmRoutes } from "@elastic/apm-rum-react";

import { isFeatureActiveOnCurrentFarm, useAppType, useFeatureFlag, useGetFarms, useUserContext } from "utils";

import { COUNTRY_CODE, COVER_CROP_MIXER_ROUTE, FEATURE_FLAGS, FEATURE_FLAG_SLUGS, SOILSIGHT_ROUTE_PATTERN, TERRA_ROUTE } from "consts";

import { ACCOUNTING_METHODOLOGY } from "models";

// misc
import ErrorPage from "pages/app/404";
import { ConfirmAccount } from "pages/app/account/ConfirmAccount";
import { ConfirmNewEmail } from "pages/app/account/ConfirmNewEmail";
import { Password } from "pages/app/account/Password";
import { PasswordReset } from "pages/app/account/PasswordReset";
// sign in/up
import { SignIn } from "pages/app/account/SignIn";
import { SignOut } from "pages/app/account/SignOut";
import { SignUp } from "pages/app/account/SignUp";
// fields
import { AllFieldView } from "pages/app/all-field-view/AllFieldView";
import { FieldsMapView } from "pages/app/all-field-view/FieldsMapView";
import { AllFieldsDataTableView } from "pages/app/all-fields-data-table-view/AllFieldsDataTableView";
// copilot
import { CopilotPage } from "pages/app/copilot/CopilotPage";
import { CropMixComposer } from "pages/app/crop-mix-composer/CropMixComposer.tsx";
import { DigitalAgronomistOnboardingPage } from "pages/app/digital-agronomist/DigitalAgronomistOnboardingPage";
import { DigitalAgronomistPage } from "pages/app/digital-agronomist/DigitalAgronomistPage";
import { SplitSuccess } from "pages/app/fields/SplitSuccess";
// footprinting
import Footprinting from "pages/app/footprinting/Footprinting";
import FootprintingFormFlow from "pages/app/footprinting/FormFlow";
import Results from "pages/app/footprinting/Results";
import Review from "pages/app/footprinting/ReviewSubmit";
import { HistoricalPracticeDataTableView } from "pages/app/historical-practice-data-table-view/HistoricalPracticeDataTableView";
// home
import Home from "pages/app/home/Home";
import PolicyPage, { POLICY_TYPE } from "pages/app/legal/PolicyPage";
// notifications
import { Notifications } from "pages/app/notifications/Notifications";
import Page from "pages/app/pages/Page";
// banking
import { CreditNote } from "pages/app/pdfs/CreditNote";
import ScenarioAnalysisGate from "pages/app/scenario-analysis/ScenarioAnalysisGate";
import { ScenarioPdfReport } from "pages/app/scenario-analysis/ScenarioPdfReport";
import { ScenariosNextStepsPage } from "pages/app/scenario-analysis/ScenariosNextStepsPage";
import { SoilSightRouteGate } from "pages/app/scenario-analysis/SoilSightRouteGate";
import { SoilSightDesktopOnly } from "pages/app/scenario-analysis/components/SoilSightDesktopOnly";
import ScenarioLandingGate from "pages/app/scenario-landing/ScenarioLandingGate";
import { ScenarioOnboardingWrapper } from "pages/app/scenario-onboarding/ScenarioOnboardingWrapper";
import { SoilSightPreLoginGate } from "pages/app/soilsight-pre-login/SoilSightPreLoginGate";
import Tracking from "pages/app/tracking/Tracking";
import { ProfileNotifications } from "pages/app/user/Notifications";
import { ProfilePrivacy } from "pages/app/user/Privacy";
import { ProfileSettings } from "pages/app/user/Settings";
import { ProfileUpdate } from "pages/app/user/Update";
import { UpdateEmail } from "pages/app/user/UpdateEmail";
import { UpdatePassword } from "pages/app/user/UpdatePassword";
import { Profile } from "pages/app/user/User";
// Wallet
import { BankingDetails } from "pages/app/wallet/BankingDetails";
import { Wallet } from "pages/app/wallet/Wallet";

export const AppRoutes = () => {
  const appType = useAppType();
  const { user } = useUserContext();

  const { data: farms, isFetched } = useGetFarms();

  const { isEnabled: isViewSwitcherEnabled } = useFeatureFlag(FEATURE_FLAGS.FIELD_MANAGEMENT_VIEW_SWITCHER);
  const { isEnabled: isCopilotEnabled } = useFeatureFlag(FEATURE_FLAGS.COPILOT);

  const currentFarm = farms?.find((farm) => farm.id === user?.farm.id) ?? null;
  const isSoilPlus3DataGapFeatureActive = isFeatureActiveOnCurrentFarm(FEATURE_FLAG_SLUGS.SOIL_PLUS_3_DATA_GAP);

  const isInventoryProgramFarm = currentFarm?.carbonProgram?.accountingMethodology === ACCOUNTING_METHODOLOGY.INVENTORY;

  const ALL_FIELDS_PAGE_PATHS = ["/all-fields", "/all-fields/cmc", "/all-fields/cmc/:cropMixId", "/all-fields/cmc/:cropMixId/:step"];
  const CMC_PAGE_PATHS = [COVER_CROP_MIXER_ROUTE, `${COVER_CROP_MIXER_ROUTE}/:cropMixId`, `${COVER_CROP_MIXER_ROUTE}/:cropMixId/:step`];

  const Redirect = ({ link }) => {
    window.location.replace(link);
  };

  const routeList = (
    <ApmRoutes>
      {/* permanent redirects for old and important pages */}
      <Route path="/page/das-landwirtschaftsteam" element={<Redirect link="https://www.klim.eco/de/landwirte/kontakt" />} />
      <Route path="/page/webinare" element={<Redirect link="https://www.klim.eco/de/landwirte/webinare" />} />
      <Route path="/page/about-us" element={<Redirect link="https://www.klim.eco/de/about-us" />} />
      <Route path="/page/ansprechpersonen-27fc9970-55ba-461f-a467-4f4726b5ed3c" element={<Redirect link="https://www.klim.eco/de/landwirte/kontakt" />} />
      <Route
        path="/page/verifizierungen-13231cd7-77e3-413b-af93-a7bbfb6e8704"
        element={<Redirect link="https://www.klim.eco/de/landwirte/verifizierungen" />}
      />
      <Route path="/page/impressum" element={<Redirect link="https://www.klim.eco/de/impressum" />} />
      <Route path="/page/impressum" element={<Redirect link="https://www.klim.eco/de/impressum" />} />
      <Route path="/page/nutzungsbedingungen" element={<Redirect link="https://www.klim.eco/de/landwirte/nutzungsbedingungen" />} />
      <Route path="/page/terms-of-use" element={<Redirect link="https://www.klim.eco/de/landwirte/nutzungsbedingungen" />} />
      <Route path="/page/co-nowego-podsumowanie-c2552d4c-4294-430c-a270-f8df59309dd5" element={<Redirect link="https://www.klim.eco" />} />
      <Route path="/page/soil-teilnahmebedingungen" element={<Redirect link="https://www.klim.eco/de/landwirte/nutzungsbedingungen" />} />

      {/* redirects */}
      <Route path="/" element={<Navigate replace to="/home" />} />
      <Route path="/content-onboard" element={<Navigate replace to="/home" />} />
      <Route path="/search/articles" exact element={<Navigate replace to="/search" />} />
      <Route path="/progress/wallet" exact element={<Navigate replace to="/wallet" />} />
      <Route path="/rewards" exact element={<Navigate replace to="/home" />} />
      <Route path="/progress" exact element={<Navigate replace to="/home" />} />
      <Route path="/impact" exact element={<Navigate replace to="/home" />} />
      <Route path="/confirm-account" exact element={<Navigate replace to="/sign-in" />} />
      <Route path="/farm-onboard" exact element={<Navigate replace to="/home" />} />
      <Route path="/dashboard" exact element={<Navigate replace to="/home" />} />
      <Route path="/notes" exact element={<Home />} />
      <Route path="/note/:link" exact element={<Home />} />
      <Route path="/profile/sign-in" element={<Navigate replace to="/sign-in" />} />
      <Route path="/profile/sign-up" element={<Navigate replace to="/sign-up" />} />

      {/* TODO: enable when removing crop explorer */}
      {/* <Route path="/crop-explorer" exact element={<Navigate replace to="/home" />} /> */}

      {/* sign in/up */}
      <Route path="/sign-in/:type?" exact element={<SignIn />} />
      <Route path="/sign-up/:type?" exact element={<SignUp />} />
      <Route path="/confirm-account/:type?/:token" exact element={<ConfirmAccount />} />
      <Route path="/confirm-new-email/:email/:token" exact element={<ConfirmNewEmail />} />
      <Route path="/password" exact element={<Password />} />
      <Route path="/password-reset/:token" exact element={<PasswordReset />} />
      <Route path="/sign-out" exact element={<SignOut />} />
      <Route path="/manage-notifications/:userId?" exact element={<ProfileNotifications />} />

      {/* home */}
      <Route path="/home" exact element={<Home />} />

      {/* link tracking */}
      <Route path="/t/:token" exact element={<Tracking />} />

      {/* farms */}
      <Route path="/farm" exact element={<Navigate replace to="/all-fields" />} />
      <Route path="/farm/field/:field1Id/:field2Id/split-success" exact element={<SplitSuccess />} />

      {/* content */}
      <Route path="/page/:link" exact element={<Page />} />

      {/* footprinting */}
      <Route path="/footprinting/form" exact element={<FootprintingFormFlow />} />
      <Route path="/footprinting/results" exact element={<Results />} />
      <Route path="/footprinting/review/:year" exact element={<Review />} />
      <Route path="/footprinting" exact element={<Footprinting />} />

      {/* profile */}
      <Route path="/user" exact element={<Profile />} />
      <Route path="/user/update" exact element={<ProfileUpdate />} />
      <Route path="/user/settings" exact element={<ProfileSettings />} />
      <Route path="/user/change-email" exact element={<UpdateEmail />} />
      <Route path="/user/change-password" exact element={<UpdatePassword />} />
      {appType.mobile ? (
        <Route path="/notifications" exact element={<Notifications />} />
      ) : (
        <Route path="/notifications" element={<Navigate replace to="/" />} />
      )}
      <Route path="/manage-privacy" exact element={<ProfilePrivacy />} />

      {/* wallet */}
      <Route path="/wallet" exact element={<Wallet />} />
      <Route path="/banking-details" exact element={<BankingDetails />} />
      <Route path="/credit-note/:token" exact element={<CreditNote />} />

      {/* legal */}
      <Route path="/privacy-policy" exact element={<PolicyPage policyType={POLICY_TYPE.PRIVACY} />} />
      <Route path="/cookie-policy" exact element={<PolicyPage policyType={POLICY_TYPE.COOKIE} />} />

      {/* digital-agronomist */}
      <Route
        path="/side-crop-recommender"
        exact
        element={user?.farm?.country === COUNTRY_CODE.POLAND ? <Navigate replace to="/home" /> : <DigitalAgronomistPage />}
      />
      <Route
        path="/side-crop-recommender/onboarding"
        exact
        element={user?.farm?.country === COUNTRY_CODE.POLAND ? <Navigate replace to="/home" /> : <DigitalAgronomistOnboardingPage />}
      />

      {/* historical practice data table view (for soil+ 3.0 data collection) */}
      <Route
        path="/historical-data"
        exact
        element={
          isFetched ? (
            // if the farm is assigned to an inventory program or the soil+ 3.0 feature flag is not active, there's no data collection
            isInventoryProgramFarm || !isSoilPlus3DataGapFeatureActive ? (
              <Navigate replace to="/all-fields" />
            ) : (
              <HistoricalPracticeDataTableView />
            )
          ) : null
        }
      />
      {/* all fields practice data table view (no carbon-program conditions) */}
      <Route path="/all-fields/table-view" exact element={isFetched ? <AllFieldsDataTableView /> : null} />
      {/* new all-fields view */}
      {ALL_FIELDS_PAGE_PATHS.map((path) => (
        <Route key={path} path={path} exact element={isFetched ? <AllFieldView /> : null} />
      ))}
      {isViewSwitcherEnabled && <Route path="/all-fields/map-view" exact element={isFetched ? <FieldsMapView /> : null} />}
      {CMC_PAGE_PATHS.map((path) => (
        <Route key={path} path={path} exact element={<CropMixComposer visible />} />
      ))}

      {/* copilot */}
      {isCopilotEnabled && <Route path={TERRA_ROUTE} exact element={<CopilotPage />} />}

      {/* scenario analysis — SoilSightRouteGate waits for the flag, then shows the flow or sends the user home */}
      <Route element={<SoilSightRouteGate />}>
        <Route element={<SoilSightDesktopOnly />}>
          <Route path="/scenarios" exact element={<ScenarioAnalysisGate />} />
          <Route path="/scenarios-landing-page" exact element={<ScenarioLandingGate />} />
          <Route path="/scenarios-onboarding/:step" exact element={<ScenarioOnboardingWrapper />} />
          <Route path="/scenarios-next-steps" exact element={<ScenariosNextStepsPage />} />
          <Route path="/scenarios-pdf" exact element={<ScenarioPdfReport />} />
        </Route>
      </Route>

      {/* We always mount the acquisition flow. SoilSightPreLoginGate routes signed-in visitors away. */}
      <Route path={SOILSIGHT_ROUTE_PATTERN} element={<SoilSightPreLoginGate />} />

      {/* if no matching routes, default to app sign in */}
      <Route path="*" exact element={<ErrorPage />} />
    </ApmRoutes>
  );

  return routeList;
};
